import type { TextAreaProps } from 'antd/es/input';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { useRef, type ReactNode } from 'react';

import { InputTextarea } from '../Ant';
import { useFitContentMaxHeight } from './useFitContentMaxHeight';

// Measured at the default task-pane width in Poppins 12px.
const APPROX_CHARS_PER_ROW = 42;

type TextAreaFieldProps = Omit<TextAreaProps, 'autoSize' | 'rows' | 'showCount' | 'maxLength'> & {
  maxLength: number;
  minRows?: number;
  /** Rows the field grows to on its own before the limit no longer fits and it becomes drag-resizable. */
  maxAutoRows?: number;
  /** Content shown on the left of the bottom strip, opposite the character count. */
  footer?: ReactNode;
};

export const resolveRowsForLimit = (maxLength: number) =>
  Math.ceil(maxLength / APPROX_CHARS_PER_ROW);

const TextAreaField: React.FC<TextAreaFieldProps> = ({
  maxLength,
  minRows = 3,
  maxAutoRows = 6,
  footer,
  ...rest
}) => {
  const textareaRef = useRef<TextAreaRef>(null);
  const isResizable = resolveRowsForLimit(maxLength) > maxAutoRows;
  const { maxHeight: resizeMaxHeight, onTextChange } = useFitContentMaxHeight(
    textareaRef,
    maxLength,
    isResizable,
    rest.value,
  );
  const wrapperClassName = ['relative textarea-field', isResizable && 'resizable']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClassName}>
      <InputTextarea
        {...rest}
        ref={textareaRef}
        onChange={(e) => {
          rest.onChange?.(e);
          onTextChange();
        }}
        maxLength={maxLength}
        showCount={true}
        {...(isResizable
          ? {
              rows: minRows,
              $resizeMinRows: minRows,
              styles: {
                ...rest.styles,
                textarea: { ...rest.styles?.textarea, maxHeight: resizeMaxHeight },
              },
            }
          : { autoSize: { minRows, maxRows: maxAutoRows } })}
      />

      <div className="d-flex justify-between items-center textarea-field-footer w-full">
        {footer}
      </div>
    </div>
  );
};

export default TextAreaField;
